import React from 'react';
import {Box, Typography} from '@mui/material';
import {FieldInputProps, FieldMetaProps} from 'formik';
import {Pot} from '../../../schema-types/Pot';
import {getCurrencyFloat, getCurrencyString} from '../../../utils/currencyFormatter';
import {getTotalTransferAmount, getTransferDestinationFixed} from './TransferHelpers';
import {mainPlanTypeType, recommendFieldsType} from '../CreateRecommend/recommendFields';
import {transferFieldsType} from './transferFields';
import type {newPlanFields} from '../RecommendsSetUpNew/setUpNewFields';

import {
  getSummary,
  noSubplansMessage,
} from '../CreateRecommend/RecommendSourceDestination/naming';
import {hasWrapper} from '../CreateRecommend/RecommendSourceDestination/utils';
import {getValidation} from '../CreateRecommend/RecommendSourceDestination/validators';
import {TransferNotSubplanDestinationAmounts} from '../CreateRecommend/RecommendSourceDestination/TransferNotSubplanDestinationAmounts';
import {TransferExistingSubplans} from './TransferExistingSubplans';
import {getSavedChildSubPlans} from '../RecommendsSetUpNew/ListNewSubPlans';
import {includesSaltus} from '../../../utils/includesSaltus';
import {NEW_PLAN_ID} from '../CreateRecommend/RecommendSourceDestination/constants';
import {NewPlan} from '../RecommendsSetUpNew/NewPlan';
import {OVRecommendation} from '../../../schema-types/OVRecommendation';
import RecommendPlanForm from '../CreateRecommend/RecommendSourceDestination/RecommendPlanForm';

const TransferExtraText: React.FC<{
  pot?: Pot;
  newPlan?: boolean;
  transferDestAccountFieldObj: FieldInputProps<string | undefined>;
  transferSourcesFieldObj: FieldInputProps<
    transferFieldsType['transferSources'] | undefined
  >;
}> = ({pot, newPlan, transferDestAccountFieldObj, transferSourcesFieldObj}) => {
  const {id, totalValue = 0} = pot || {};
  if (newPlan) {
    if (transferDestAccountFieldObj.value !== NEW_PLAN_ID) {
      return null;
    }
  } else if (id !== transferDestAccountFieldObj.value) {
    return null;
  }
  return (
    <Typography>
      <strong data-testid="extra-text-id">{` - Post Contribution: ${getCurrencyString(
        getCurrencyFloat(totalValue) +
          getTotalTransferAmount(Object.values(transferSourcesFieldObj.value || {}))
      )}`}</strong>
    </Typography>
  );
};

interface TransferDestinationPlanFormProps {
  pot: Pot;
  destPots: Pot[];
  mainPlanType: mainPlanTypeType;
  values: recommendFieldsType;
  transferDestAccountFieldObj: FieldInputProps<string | undefined>;
  transferSourcesFieldObj: FieldInputProps<
    transferFieldsType['transferSources'] | undefined
  >;
  transferDestAccountFieldMeta: FieldMetaProps<string | undefined>;
  recommendDetails: OVRecommendation | null;
  reviewId: string;
  isSelected: boolean;
  handleChange: (event: React.ChangeEvent<any> | null, value: string) => void;
}

export const TransferDestinationPlanForm: React.FC<TransferDestinationPlanFormProps> = ({
  pot,
  destPots,
  mainPlanType,
  values,
  transferDestAccountFieldObj,
  transferSourcesFieldObj,
  transferDestAccountFieldMeta,
  recommendDetails,
  reviewId,
  isSelected,
  handleChange,
}) => {
  const fixed = getTransferDestinationFixed(recommendDetails);
  const showSubPlans =
    pot.children?.length &&
    hasWrapper(mainPlanType) &&
    getSavedChildSubPlans(destPots, pot.id) &&
    includesSaltus(pot.productProvider?.name);

  return (
    <RecommendPlanForm
      id={pot.id}
      reccId={pot.originalOvrId ?? null}
      fixed={fixed}
      summary={
        <Box display="flex" flexDirection="row">
          {getSummary(pot)}
          &nbsp;
          <TransferExtraText
            pot={pot}
            transferDestAccountFieldObj={transferDestAccountFieldObj}
            transferSourcesFieldObj={transferSourcesFieldObj}
          />
        </Box>
      }
      details={
        showSubPlans ? (
          <div />
        ) : (
          <TransferNotSubplanDestinationAmounts
            disabled={false}
            potInner={pot}
            formikKeyPrefix="transfer.destinationValues"
            mainPlanType={mainPlanType}
          />
        )
      }
      curoId={pot.curoId}
      subItems={
        showSubPlans ? (
          <TransferExistingSubplans
            parentId={pot.id}
            showEditing
            pots={destPots}
            mainPlanType={mainPlanType}
          />
        ) : (
          noSubplansMessage(values)
        )
      }
      validation={isSelected ? getValidation(transferDestAccountFieldMeta) : undefined}
      sameReview={pot.review?.id === reviewId}
      isSelected={isSelected}
      isSubplan={false}
      handleChange={handleChange}
    />
  );
};

interface TransferNewPlanFormProps {
  values: recommendFieldsType;
  transferDestAccountFieldObj: FieldInputProps<string | undefined>;
  transferSourcesFieldObj: FieldInputProps<
    transferFieldsType['transferSources'] | undefined
  >;
  contactName: string;
  setUpNewFieldMeta: FieldMetaProps<newPlanFields | undefined>;
  recommendDetails: OVRecommendation | null;
  isSelected: boolean;
  handleChange: (event: React.ChangeEvent<any> | null, value: string) => void;
}

export const TransferNewPlanForm: React.FC<TransferNewPlanFormProps> = ({
  values,
  transferDestAccountFieldObj,
  transferSourcesFieldObj,
  contactName,
  setUpNewFieldMeta,
  recommendDetails,
  isSelected,
  handleChange,
}) => {
  const fixed = getTransferDestinationFixed(recommendDetails);

  return (
    <RecommendPlanForm
      id={NEW_PLAN_ID}
      reccId={null}
      newPlanPotId={values.transfer?.newPlan?.existingPotId}
      sameReview={false}
      fixed={fixed}
      summary={
        <Box display="flex" width="100%" flexDirection="row">
          <Typography>A New Plan</Typography>&nbsp;
          <TransferExtraText
            newPlan
            transferDestAccountFieldObj={transferDestAccountFieldObj}
            transferSourcesFieldObj={transferSourcesFieldObj}
          />
        </Box>
      }
      isNewWrapperPlanForTransferOrContribution
      details={
        <NewPlan
          context="transfer"
          formikKeyPrefix="transfer.newPlan"
          contactName={contactName}
        />
      }
      isSelected={isSelected}
      isSubplan={false}
      handleChange={handleChange}
    />
  );
};
